import { type JSX } from 'react';
import ReactSelect, {
  components,
  type ActionMeta,
  type GroupBase,
  type OnChangeValue,
  type OptionProps,
  type Props,
} from 'react-select';
import z from 'zod';
import { Icon } from '../assets/icons';
import { Z_INDEXES } from '../constants/styles';
import { cn } from '../lib/cn';
import { Checkbox } from './checkbox';
import { Radio } from './radio';
import { Tag } from './tag';
import { TooltipElement } from './tooltip';
import { Typography } from './typography';

export const SelectOptionContract = z.object({
  value: z.string(),
  label: z.string(),
});
export type SelectOption = z.infer<typeof SelectOptionContract>;

const Option = <T extends SelectOption>(
  props: OptionProps<T, boolean, GroupBase<T>>,
  isMulti: boolean,
  selectAll: boolean,
  isAllSelected: boolean
) => {
  const isSelectAllOption = selectAll && props.data.value === 'select-all';
  const checked = isSelectAllOption ? isAllSelected : props.isSelected;

  return (
    <components.Option
      {...props}
      className={cn(
        checked &&
          'bg-neutral-1! flex items-center text-inherit! hover:bg-blue-100!'
      )}
    >
      {isMulti ? (
        <Checkbox
          className="mr-2"
          checked={checked}
          label={props.label}
          onChange={() => null}
        />
      ) : (
        <Radio
          className="mr-2"
          checked={checked}
          label={props.label}
          onChange={() => null}
        />
      )}
    </components.Option>
  );
};

type BaseSelectProps<T extends SelectOption, IsMulti extends boolean> = Props<
  T,
  IsMulti,
  GroupBase<T>
>;

type SelectStyles<
  T extends SelectOption,
  IsMulti extends boolean,
> = NonNullable<BaseSelectProps<T, IsMulti>['styles']>;

/**
 * Layers caller-provided `styles` on top of this component's defaults instead of
 * letting either side clobber the other: for each style key the default runs
 * first, then the override receives its result as `base`.
 */
const mergeSelectStyles = <T extends SelectOption, IsMulti extends boolean>(
  defaults: SelectStyles<T, IsMulti>,
  overrides?: SelectStyles<T, IsMulti>
): SelectStyles<T, IsMulti> => {
  if (!overrides) return defaults;

  const keys = new Set([...Object.keys(defaults), ...Object.keys(overrides)]);
  const merged: SelectStyles<T, IsMulti> = {};

  for (const key of keys) {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const defaultFn = (defaults as any)[key];
    const overrideFn = (overrides as any)[key];

    (merged as any)[key] = (base: any, state: any) => {
      const withDefaults = defaultFn ? defaultFn(base, state) : base;
      return overrideFn ? overrideFn(withDefaults, state) : withDefaults;
    };
    /* eslint-enable @typescript-eslint/no-explicit-any */
  }

  return merged;
};

type ExtraProps = {
  label?: string;
  withTags?: boolean;
  readOnly?: boolean;
  withGutter?: boolean;
  clearedValue?: boolean;
  required?: boolean;
  selectAll?: boolean;
  infoText?: string;
  error?: boolean;
  errorText?: string;
  /**
   * Portals the menu to document.body (default). Set to false when the Select is
   * rendered inside a Radix Dialog/Popover — those hide/inert everything on
   * document.body outside their own container, which silently swallows clicks on
   * a portaled menu (they appear to "click through" to whatever is behind it).
   */
  menuPortal?: boolean;
};

function SelectImpl<T extends SelectOption, IsMulti extends boolean>({
  isSearchable = false,
  placeholder = 'Select...',
  isMulti = false as IsMulti,
  withTags = false,
  readOnly = false,
  withGutter = true,
  clearedValue = false,
  required = false,
  selectAll = false,
  error = false,
  menuPortal = true,
  infoText,
  label,
  value,
  options,
  className,
  errorText,
  styles,
  onChange,
  ...props
}: Omit<BaseSelectProps<T, IsMulti>, 'defaultValue'> & ExtraProps) {
  const selectAllOption = {
    label: 'Select All',
    value: 'select-all',
  } as T;

  const flatOptions = (options ?? []) as T[];
  const allOptions = selectAll
    ? ([selectAllOption, ...flatOptions] as T[])
    : flatOptions;

  const isAllSelected =
    isMulti && Array.isArray(value) && value.length === flatOptions.length;

  const hasValue = isMulti
    ? Array.isArray(value) && value.length > 0
    : value != null;

  const handleChange = (
    newValue: OnChangeValue<T, IsMulti>,
    actionMeta: ActionMeta<T>
  ) => {
    if (!isMulti || !Array.isArray(newValue) || !selectAll) {
      onChange?.(newValue, actionMeta);
      return;
    }

    const lastSelected = newValue[newValue.length - 1];

    if (lastSelected?.value === 'select-all') {
      const finalValue = isAllSelected ? [] : flatOptions;
      onChange?.(
        finalValue as unknown as OnChangeValue<T, IsMulti>,
        actionMeta
      );
      return;
    }

    const filtered = newValue.filter((opt) => opt.value !== 'select-all');
    onChange?.(filtered as unknown as OnChangeValue<T, IsMulti>, actionMeta);
  };

  const maxTags = 4;

  return (
    <div
      data-field-error={error ? 'true' : undefined}
      className={cn('w-full', 'flex', 'flex-col', 'gap-2', className)}
    >
      {/* Rendered only when it has content -- an empty label row still costs the
          wrapper's `gap-2`, which pushed the control 8px down and misaligned the
          header facility dropdown against the date pickers beside it. Matches
          how Input handles its own label. */}
      {(label || required || infoText) && (
        <div className="flex items-center gap-1">
          {required && (
            <Icon name="required" className="text-dust-red-5" size={10} />
          )}
          {label && <Typography>{label}</Typography>}
          {infoText && <TooltipElement content={infoText} />}
        </div>
      )}

      <ReactSelect
        {...props}
        classNamePrefix="react-select"
        value={value}
        isMulti={isMulti}
        options={allOptions}
        placeholder={placeholder}
        hideSelectedOptions={false}
        isSearchable={isSearchable}
        closeMenuOnSelect={!isMulti}
        menuIsOpen={readOnly ? false : undefined}
        controlShouldRenderValue={!clearedValue && !withTags}
        menuPortalTarget={menuPortal ? document.body : undefined}
        components={{
          ClearIndicator: undefined,
          IndicatorSeparator: undefined,
          DropdownIndicator: readOnly
            ? undefined
            : components.DropdownIndicator,
          Option: (props) => Option(props, isMulti, selectAll, isAllSelected),
          MultiValue: ({ getValue, index }) =>
            getValue()[index]['label'] +
            `${getValue().length - 1 === index ? '' : ', '}`,
          MultiValueRemove: () => undefined,
        }}
        styles={mergeSelectStyles<T, IsMulti>(
          /*
           * react-select paints its surfaces with inline styles (white menu,
           * #DEEBFF hover, #333 text), which the dark theme's token swap cannot
           * reach — in dark mode the menu stayed white while its text inherited
           * the page's near-white, so options were invisible. Every color is a
           * token here instead. In light mode they land within a shade of
           * react-select's own defaults.
           */
          {
            control: (base, state) => ({
              ...base,
              minHeight: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: 'var(--color-neutral-1)',
              ...(state.isFocused
                ? {}
                : { borderColor: 'var(--color-neutral-5)' }),
            }),
            input: (base) => ({ ...base, color: 'var(--color-neutral-12)' }),
            valueContainer: (base) => ({
              ...base,
              paddingLeft: 10,
            }),
            placeholder: (base) => ({
              ...base,
              fontSize: 14,
              fontWeight: 400,
              color: 'var(--color-neutral-7)',
            }),
            multiValue: (base) => ({ ...base, fontSize: 14 }),
            singleValue: (base) => ({
              ...base,
              fontSize: 14,
              color: 'var(--color-neutral-12)',
            }),
            indicatorsContainer: (base) => ({ ...base, height: 40 }),
            menuPortal: (base) => ({ ...base, zIndex: Z_INDEXES.POPUP }),
            menu: (base) => ({
              ...base,
              marginTop: 4,
              zIndex: Z_INDEXES.POPUP,
              backgroundColor: 'var(--color-neutral-1)',
              color: 'var(--color-neutral-12)',
              boxShadow:
                '0 0 0 1px var(--color-neutral-5), 0 4px 11px rgb(0 0 0 / 0.1)',
            }),
            option: (base, state) => ({
              ...base,
              color: 'var(--color-neutral-12)',
              backgroundColor: state.isFocused
                ? 'var(--color-primary-1)'
                : 'var(--color-neutral-1)',
              ':active': { backgroundColor: 'var(--color-primary-2)' },
            }),
            noOptionsMessage: (base) => ({
              ...base,
              color: 'var(--color-neutral-7)',
            }),
            dropdownIndicator: (base, state) => ({
              ...base,
              transition: 'transform 200ms ease',
              transform: state.selectProps.menuIsOpen
                ? 'rotate(180deg)'
                : 'rotate(0deg)',
            }),
          },
          styles
        )}
        onChange={handleChange}
      />

      {withTags && hasValue && (
        <div className={cn(withGutter && 'min-h-6')}>
          {isMulti && Array.isArray(value) ? (
            <div className="flex flex-wrap gap-1">
              {value.slice(0, maxTags).map((option) => (
                <Tag
                  variant="default"
                  id={option.value}
                  key={option.value}
                  text={option.label}
                  onClose={() =>
                    onChange?.(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      value.filter((v) => v.value !== option.value) as any,
                      {
                        action: 'remove-value',
                        option,
                        removedValue: option,
                      }
                    )
                  }
                />
              ))}

              {value.length > maxTags && (
                <Tag
                  id="+x-more"
                  variant="default"
                  text={`+${value.length - maxTags} more`}
                />
              )}
            </div>
          ) : (
            !isMulti &&
            value &&
            typeof value === 'object' &&
            'value' in value && (
              <Tag
                variant="default"
                id={value.value}
                key={value.value}
                text={value.label}
                onClose={() =>
                  onChange?.(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    null as any,
                    {
                      action: 'clear',
                      removedValues: [value],
                    }
                  )
                }
              />
            )
          )}
        </div>
      )}

      {errorText && error && (
        <div className={'min-h-6 w-full'}>
          <Typography
            className="text-dust-red-5"
            variant="footnote-description"
          >
            {errorText}
          </Typography>
        </div>
      )}
    </div>
  );
}

export const Select = SelectImpl as <
  T extends SelectOption,
  IsMulti extends boolean = false,
>(
  props: BaseSelectProps<T, IsMulti> & ExtraProps
) => JSX.Element;
